import { Client, StompSubscription, IMessage } from '@stomp/stompjs';
import * as SockJS from 'sockjs-client';
import { debugLogger } from '../utils/debugLogger';

type MessageHandler = (message: any) => void;
type ConnectionStateHandler = (connected: boolean) => void;

interface Subscription {
  destination: string;
  handler: MessageHandler;
  subscription?: StompSubscription;
}

/**
 * Singleton WebSocket Manager
 * Manages a single WebSocket connection for the entire application
 * Reuses the same connection for all WebSocket operations
 */
class WebSocketManager {
  private static instance: WebSocketManager | null = null;
  private client: Client | null = null;
  private subscriptions: Map<string, Subscription> = new Map();
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set();
  private isConnected: boolean = false;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 5000;

  private constructor() {
    debugLogger.log('WebSocketManager', 'Instance created');
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  /**
   * Connect to WebSocket server (only once)
   */
  public connect(token?: string): void {
    // Prevent multiple connections
    if (this.isConnected || this.isConnecting) {
      debugLogger.log('WebSocketManager', 'Already connected or connecting', {
        isConnected: this.isConnected,
        isConnecting: this.isConnecting
      });
      return;
    }

    debugLogger.log('WebSocketManager', 'Initiating connection', { token: !!token });
    this.isConnecting = true;

    try {
      const wsUrl = `${process.env.REACT_APP_API_URL}/ws`;
      
      // Create SockJS instance with token in query param if available
      const socketUrl = token ? `${wsUrl}?token=${token}` : wsUrl;
      const socket = new (SockJS as any)(socketUrl);

      this.client = new Client({
        webSocketFactory: () => socket as any,
        reconnectDelay: this.reconnectDelay,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        debug: (str) => {
          debugLogger.log('STOMP', str);
        },
        onConnect: () => {
          debugLogger.log('WebSocketManager', 'Connected successfully');
          this.isConnected = true;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.notifyConnectionState(true);
          
          // Re-subscribe to all previous subscriptions
          this.resubscribeAll();
        },
        onStompError: (frame) => {
          debugLogger.log('WebSocketManager', 'STOMP error', frame);
          this.isConnected = false;
          this.isConnecting = false;
          this.notifyConnectionState(false);
          
          // Retry connection if not exceeded max attempts
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            debugLogger.log('WebSocketManager', `Retry connection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
            setTimeout(() => {
              this.client?.activate();
            }, this.reconnectDelay);
          }
        },
        onWebSocketClose: () => {
          debugLogger.log('WebSocketManager', 'WebSocket closed');
          this.isConnected = false;
          this.isConnecting = false;
          this.notifyConnectionState(false);
        },
        onWebSocketError: (error) => {
          debugLogger.log('WebSocketManager', 'WebSocket error', error);
          this.isConnected = false;
          this.isConnecting = false;
        }
      });

      // Set auth header if token is provided
      if (token) {
        this.client.connectHeaders = {
          Authorization: `Bearer ${token}`
        };
      }

      this.client.activate();
    } catch (error) {
      debugLogger.log('WebSocketManager', 'Failed to connect', error);
      this.isConnecting = false;
      this.notifyConnectionState(false);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    debugLogger.log('WebSocketManager', 'Disconnecting');
    
    // Unsubscribe all
    this.subscriptions.forEach((sub) => {
      if (sub.subscription) {
        sub.subscription.unsubscribe();
      }
    });
    this.subscriptions.clear();

    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.notifyConnectionState(false);
  }

  /**
   * Subscribe to a topic
   */
  public subscribe(destination: string, handler: MessageHandler): string {
    const subscriptionId = `${destination}_${Date.now()}`;
    
    debugLogger.log('WebSocketManager', 'Subscribing', { destination, subscriptionId });

    const subscription: Subscription = {
      destination,
      handler
    };

    // If already connected, subscribe immediately
    if (this.isConnected && this.client) {
      try {
        subscription.subscription = this.client.subscribe(destination, (message: IMessage) => {
          try {
            const data = JSON.parse(message.body);
            handler(data);
          } catch (error) {
            debugLogger.log('WebSocketManager', 'Failed to parse message', error);
          }
        });
        debugLogger.log('WebSocketManager', 'Subscribed successfully', { destination });
      } catch (error) {
        debugLogger.log('WebSocketManager', 'Failed to subscribe', error);
      }
    }

    this.subscriptions.set(subscriptionId, subscription);
    return subscriptionId;
  }

  /**
   * Unsubscribe from a topic
   */
  public unsubscribe(subscriptionId: string): void {
    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription) {
      if (subscription.subscription) {
        subscription.subscription.unsubscribe();
        debugLogger.log('WebSocketManager', 'Unsubscribed', { subscriptionId });
      }
      this.subscriptions.delete(subscriptionId);
    }
  }

  /**
   * Publish a message to a destination
   */
  public publish(destination: string, body: any): void {
    if (!this.isConnected || !this.client) {
      debugLogger.log('WebSocketManager', 'Cannot publish - not connected', { destination });
      return;
    }

    try {
      this.client.publish({
        destination,
        body: JSON.stringify(body)
      });
      debugLogger.log('WebSocketManager', 'Published message', { destination, body });
    } catch (error) {
      debugLogger.log('WebSocketManager', 'Failed to publish', error);
    }
  }

  /**
   * Add connection state listener
   */
  public addConnectionStateListener(handler: ConnectionStateHandler): void {
    this.connectionStateHandlers.add(handler);
    // Immediately notify current state
    handler(this.isConnected);
  }

  /**
   * Remove connection state listener
   */
  public removeConnectionStateListener(handler: ConnectionStateHandler): void {
    this.connectionStateHandlers.delete(handler);
  }

  /**
   * Check if connected
   */
  public getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Re-subscribe to all topics after reconnection
   */
  private resubscribeAll(): void {
    if (!this.client) return;

    debugLogger.log('WebSocketManager', 'Re-subscribing to all topics', {
      count: this.subscriptions.size
    });

    this.subscriptions.forEach((subscription) => {
      try {
        subscription.subscription = this.client!.subscribe(
          subscription.destination,
          (message: IMessage) => {
            try {
              const data = JSON.parse(message.body);
              subscription.handler(data);
            } catch (error) {
              debugLogger.log('WebSocketManager', 'Failed to parse message', error);
            }
          }
        );
      } catch (error) {
        debugLogger.log('WebSocketManager', 'Failed to re-subscribe', {
          destination: subscription.destination,
          error
        });
      }
    });
  }

  /**
   * Notify all connection state listeners
   */
  private notifyConnectionState(connected: boolean): void {
    this.connectionStateHandlers.forEach((handler) => {
      try {
        handler(connected);
      } catch (error) {
        debugLogger.log('WebSocketManager', 'Error in connection state handler', error);
      }
    });
  }
}

// Export singleton instance
export const webSocketManager = WebSocketManager.getInstance();
